/**
 * TeachersPage — `/teachers` (design: "O'qituvchilar / Following" artboard)
 *
 * Teacher directory cards with follow/unfollow, search, and tabs
 * (Kuzatuvdagilar / Hammasi). Data from /api/teachers/.
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { listTeachers, followTeacher, unfollowTeacher } from '@/api/social'
import { Avatar, SectionHeader } from '@/components/kit'
import { Search, Heart, FileText, Users } from 'lucide-react'

export default function TeachersPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [teachers, setTeachers] = useState([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState('following')
  const [search, setSearch] = useState('')

  async function load(config) {
    try {
      const { data } = await listTeachers(undefined, config)
      setTeachers(data.results ?? data ?? [])
    } catch (err) {
      if (err.code === 'ERR_CANCELED') return
    } finally {
      if (!config?.signal?.aborted) setLoading(false)
    }
  }
  useEffect(() => {
    const controller = new AbortController()
    load({ signal: controller.signal })
    return () => controller.abort()
  }, [])

  async function toggleFollow(teacher) {
    // optimistic update
    setTeachers((ts) => ts.map((x) =>
      x.id === teacher.id
        ? { ...x, is_following: !x.is_following, followers_count: x.followers_count + (x.is_following ? -1 : 1) }
        : x,
    ))
    try {
      if (teacher.is_following) await unfollowTeacher(teacher.id)
      else await followTeacher(teacher.id)
    } catch {
      load() // revert on failure
    }
  }

  const followingCount = teachers.filter((x) => x.is_following).length

  const visible = useMemo(() => {
    let base = teachers
    if (tab === 'following') base = base.filter((x) => x.is_following)
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      base = base.filter((x) => (x.full_name || '').toLowerCase().includes(q))
    }
    return base
  }, [teachers, tab, search])

  return (
    <div>
      <SectionHeader
        title={t('teachers.title', "O'qituvchilar")}
        subtitle={t('teachers.subtitle', "Siz kuzatayotgan ustozlar imtihonlarini birinchi bo'lib ko'rasiz")}
        right={
          <div className="row gap-8" style={{
            padding: '8px 14px', background: 'var(--bg-soft)', borderRadius: 999, border: '1px solid var(--border)',
          }}>
            <Search size={14} color="var(--ink-3)" />
            <input
              placeholder={t('teachers.search', "O'qituvchi izlash...")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ border: 0, background: 'transparent', outline: 'none', font: 'inherit', fontSize: 13, width: 160, color: 'var(--ink)' }}
            />
          </div>
        }
      />

      {/* Tabs */}
      <div className="row gap-4" style={{ marginBottom: 16, borderBottom: '1px solid var(--border)' }}>
        {[
          { k: 'following', l: `${t('teachers.following', 'Kuzatuvdagilar')} (${followingCount})` },
          { k: 'all', l: t('teachers.discover', 'Hammasi') },
        ].map((tb) => (
          <button key={tb.k} onClick={() => setTab(tb.k)} style={{
            padding: '10px 16px', background: 'none', border: 0,
            borderBottom: tab === tb.k ? '2px solid var(--coral)' : '2px solid transparent',
            color: tab === tb.k ? 'var(--ink)' : 'var(--ink-3)',
            fontWeight: tab === tb.k ? 700 : 500, fontSize: 13, cursor: 'pointer', marginBottom: -1,
            fontFamily: 'inherit',
          }}>{tb.l}</button>
        ))}
      </div>

      {loading && (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--coral-soft)', borderTopColor: 'var(--coral)' }} />
        </div>
      )}

      {!loading && visible.length === 0 && (
        <div className="card" style={{ padding: 32, borderRadius: 22, textAlign: 'center', borderStyle: 'dashed', boxShadow: 'none' }}>
          <Users size={36} strokeWidth={1.5} style={{ margin: '0 auto 10px', color: 'var(--muted)' }} />
          <div className="t-sm t-mut">
            {tab === 'following'
              ? t('teachers.emptyFollowing', "Hali hech kimni kuzatmaysiz — Hammasi bo'limidan toping.")
              : t('teachers.empty', "O'qituvchi topilmadi.")}
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {visible.map((tc, i) => (
          <div
            key={tc.id}
            onClick={() => navigate(`/teachers/${tc.id}`)}
            className="card"
            style={{ padding: 18, borderRadius: 22, cursor: 'pointer' }}
          >
            <div className="row gap-12">
              <Avatar name={tc.full_name} size={56} idx={i + 2} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="fw-7">{tc.full_name}</div>
                <div className="t-xs t-mut">@{tc.username}</div>
              </div>
            </div>
            <div className="row gap-16" style={{ marginTop: 14 }}>
              <div>
                <div className="h-display" style={{ fontSize: 18, color: 'var(--coral)' }}>{tc.exams_count}</div>
                <div className="t-xs t-mut">{t('teachers.exams', 'imtihon')}</div>
              </div>
              <div>
                <div className="h-display" style={{ fontSize: 18 }}>{tc.followers_count}</div>
                <div className="t-xs t-mut">{t('teachers.followers', 'kuzatuv')}</div>
              </div>
            </div>
            <div className="row gap-8" style={{ marginTop: 14 }}>
              <button
                onClick={(e) => { e.stopPropagation(); toggleFollow(tc) }}
                className={'btn ' + (tc.is_following ? 'btn-soft' : 'btn-primary')}
                style={{ flex: 1, justifyContent: 'center' }}
              >
                <Heart size={14} fill={tc.is_following ? 'currentColor' : 'none'} color={tc.is_following ? undefined : '#fff'} />
                {tc.is_following ? t('teachers.followingBtn', 'Kuzatilmoqda') : t('teachers.followBtn', 'Kuzatish')}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
